import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

interface Funnel {
  id: string;
  name: string;
  business_type: string;
  target_audience: string;
  budget: number;
  created_at: string;
  stages: any;
}

// 개발자 모드용 샘플 데이터
const SAMPLE_FUNNELS: Funnel[] = [
  {
    id: 'sample-1',
    name: '온라인 쇼핑몰 마케팅 퍼널',
    business_type: '이커머스',
    target_audience: '20-30대 여성',
    budget: 5000000,
    created_at: new Date().toISOString(),
    stages: {}
  },
  {
    id: 'sample-2',
    name: 'SaaS 제품 런칭 퍼널',
    business_type: 'B2B SaaS',
    target_audience: '스타트업 창업자',
    budget: 10000000,
    created_at: new Date(Date.now() - 86400000).toISOString(),
    stages: {}
  },
  {
    id: 'sample-3',
    name: '교육 서비스 프로모션',
    business_type: '온라인 교육',
    target_audience: '대학생 및 취준생',
    budget: 3000000,
    created_at: new Date(Date.now() - 172800000).toISOString(),
    stages: {}
  }
];

export default function MyFunnelsPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [funnels, setFunnels] = useState<Funnel[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDeveloperMode, setIsDeveloperMode] = useState(false);

  useEffect(() => {
    // 개발자 모드 확인
    const devMode = localStorage.getItem('developerMode') === 'true';
    setIsDeveloperMode(devMode);

    // 인증 로딩이 끝난 후에 체크
    if (!authLoading) {
      if (!user && !devMode) {
        navigate('/login');
        return;
      }
      loadFunnels();
    }
  }, [user, authLoading, navigate]);

  const loadFunnels = async () => {
    try {
      // 개발자 모드일 때는 샘플 데이터 사용
      if (isDeveloperMode) {
        setFunnels(SAMPLE_FUNNELS);
        setLoading(false);
        return;
      }

      // user가 없으면 빈 배열 반환
      if (!user?.id) {
        setFunnels([]);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('funnels')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setFunnels(data || []);
    } catch (error) {
      console.error('Error loading funnels:', error);
      setFunnels([]);
    } finally {
      setLoading(false);
    }
  };

  const deleteFunnel = async (id: string) => {
    if (!confirm('이 퍼널을 삭제하시겠습니까?')) return;

    // 개발자 모드일 때는 로컬에서만 삭제
    if (isDeveloperMode) {
      setFunnels(funnels.filter(f => f.id !== id));
      return;
    }

    try {
      const { error } = await supabase
        .from('funnels')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setFunnels(funnels.filter(f => f.id !== id));
    } catch (error) {
      console.error('Error deleting funnel:', error);
      alert('퍼널 삭제에 실패했습니다.');
    }
  };

  const runFunnel = (funnel: Funnel) => {
    // 퍼널 데이터를 localStorage에 저장하고 awareness 페이지로 이동
    localStorage.setItem('currentFunnel', JSON.stringify(funnel));
    navigate('/awareness-campaign');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50 flex items-center justify-center pt-16" style={{ fontFamily: '"Noto Sans KR", sans-serif' }}>
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">퍼널을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50" style={{ fontFamily: '"Noto Sans KR", sans-serif' }}>
      <Header />
      
      <main className="pt-28 sm:pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-3">
              <h1 className="text-4xl font-bold text-gray-900">저장된 퍼널</h1>
              {isDeveloperMode && (
                <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-semibold rounded-full">
                  개발자 모드
                </span>
              )}
            </div>
            <p className="text-lg text-gray-600">생성하고 저장한 마케팅 퍼널을 관리하고 실행하세요</p>
          </div>

          {funnels.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
              <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-folder-open-line text-4xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">저장된 퍼널이 없습니다</h3>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                퍼널 생성기에서 새로운 마케팅 퍼널을 만들어보세요
              </p>
              <button
                onClick={() => navigate('/funnel-generator')}
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-teal-500 to-blue-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-add-line text-xl"></i>
                퍼널 생성하기
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {funnels.map((funnel) => (
                <div
                  key={funnel.id}
                  className="bg-white rounded-2xl shadow-xl border-2 border-gray-100 p-6 hover:shadow-2xl hover:border-teal-200 transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{funnel.name}</h3>
                      <p className="text-sm text-gray-600">{funnel.business_type}</p>
                    </div>
                    <button
                      onClick={() => deleteFunnel(funnel.id)}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200 cursor-pointer"
                    >
                      <i className="ri-delete-bin-line text-lg"></i>
                    </button>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-group-line text-teal-500"></i>
                      <span>{funnel.target_audience}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-money-dollar-circle-line text-teal-500"></i>
                      <span>예산: {funnel.budget?.toLocaleString('ko-KR')}원</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-calendar-line text-teal-500"></i>
                      <span>{new Date(funnel.created_at).toLocaleDateString('ko-KR')}</span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => runFunnel(funnel)}
                      className="flex-1 px-4 py-3 bg-gradient-to-r from-teal-500 to-blue-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer"
                    >
                      <i className="ri-play-circle-line text-lg"></i>
                      자동 실행
                    </button>
                    <button
                      onClick={() => {
                        localStorage.setItem('editFunnel', JSON.stringify(funnel));
                        navigate('/funnel-generator');
                      }}
                      className="px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-edit-line text-lg"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
